import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { courseAPI } from '../../services/api';

// Fetch all courses with optional relationships
export const fetchCourses = createAsyncThunk('course/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await courseAPI.getAll(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch courses');
  }
});

// Fetch single course with full details
export const fetchCourseById = createAsyncThunk('course/fetchById', async ({ id, withRelationships = true }, { rejectWithValue }) => {
  try {
    const response = await courseAPI.getById(id, withRelationships);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch course');
  }
});

// Create course
export const createCourse = createAsyncThunk('course/create', async (data, { rejectWithValue }) => {
  try {
    const response = await courseAPI.create(data);
    return response.data.data;
  } catch (error) {
    // Extract detailed error message
    const errorMessage = error.response?.data?.error || 
                        error.response?.data?.message || 
                        error.message || 
                        'Failed to create course';
    
    // Log for debugging
    console.error('Create course error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: errorMessage
    });
    
    return rejectWithValue(errorMessage);
  }
});

// Update course
export const updateCourse = createAsyncThunk('course/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await courseAPI.update(id, data);
    return response.data.data;
  } catch (error) {
    // Extract detailed error message
    const errorMessage = error.response?.data?.error || 
                        error.response?.data?.message || 
                        error.message || 
                        'Failed to update course';
    
    // Log for debugging
    console.error('Update course error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: errorMessage
    });
    
    return rejectWithValue(errorMessage);
  }
});

// Delete course
export const deleteCourse = createAsyncThunk('course/delete', async (id, { rejectWithValue }) => {
  try {
    await courseAPI.delete(id);
    return id;
  } catch (error) {
    // Extract detailed error message
    const errorMessage = error.response?.data?.error || 
                        error.response?.data?.message || 
                        error.message || 
                        'Failed to delete course';
    
    // Log for debugging
    console.error('Delete course error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: errorMessage
    });
    
    return rejectWithValue(errorMessage);
  }
});

const courseSlice = createSlice({
  name: 'course',
  initialState: { 
    courses: [], 
    selectedCourse: null,
    loading: false, 
    error: null, 
    pagination: null 
  },
  reducers: { 
    clearError: (state) => { state.error = null; },
    clearSelectedCourse: (state) => { state.selectedCourse = null; }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all courses
      .addCase(fetchCourses.pending, (state) => { state.loading = true; })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload.data || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCourses.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      // Fetch single course
      .addCase(fetchCourseById.pending, (state) => { state.loading = true; })
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCourse = action.payload;
      })
      .addCase(fetchCourseById.rejected, (state, action) => { 
        state.loading = false; 
        state.error = action.payload; 
      })
      // Create course
      .addCase(createCourse.fulfilled, (state, action) => { 
        state.courses.unshift(action.payload); 
      })
      // Update course
      .addCase(updateCourse.fulfilled, (state, action) => {
        const index = state.courses.findIndex(c => c.id === action.payload.id);
        if (index !== -1) state.courses[index] = action.payload;
        if (state.selectedCourse?.id === action.payload.id) {
          state.selectedCourse = action.payload;
        }
      })
      // Delete course
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.courses = state.courses.filter(c => c.id !== action.payload);
        if (state.selectedCourse?.id === action.payload) {
          state.selectedCourse = null;
        }
      });
  },
});

export const { clearError, clearSelectedCourse } = courseSlice.actions;
export default courseSlice.reducer;
